import type { Path } from 'slate'
import { Node as SlateNode } from 'slate'
import { ReactEditor, useSlateStatic } from 'slate-react'
import type { TunaEditor } from '@tunasong/plugin-lib'

/**
 * The element's current path in the document, or `undefined` if it is not in it.
 *
 * Use this instead of Plate's `usePath()` (or a path captured at render) for anything that writes
 * to, or reads around, the element. `usePath()` is memoised on the element object, so a block
 * inserted or removed above shifts the element without changing it and leaves the path stale —
 * a write `at` it lands on the neighbour (GH #1953).
 *
 * `ReactEditor.findPath` is O(depth) but reads slate-react's NODE_TO_INDEX, which lags the
 * document until the parent re-renders, so its answer is checked against the document and an
 * identity walk over the tree is the fallback.
 */
export const findElementPath = (editor: TunaEditor, element: SlateNode): Path | undefined => {
  const root = editor as unknown as SlateNode
  try {
    const path = ReactEditor.findPath(editor as unknown as ReactEditor, element)
    if (SlateNode.has(root, path) && SlateNode.get(root, path) === element) {
      return path
    }
  } catch {
    /** Not mounted (no NODE_TO_INDEX entry) — fall through to the tree walk. */
  }
  for (const [node, path] of SlateNode.nodes(root)) {
    if (node === element) {
      return path
    }
  }
  return undefined
}

/**
 * `findElementPath` for render-time reads (scale lookup, the parent line). Recomputed on every
 * render: the path depends on the whole document, not on `element`. It is a hook so a component
 * reads it through a hook call, which the React Compiler never caches — a cache keyed on
 * `[editor, element]` would reproduce `usePath()`'s staleness.
 *
 * It does not subscribe to the document: an element that does not re-render keeps what it showed,
 * which stays correct because its ancestors are unchanged by a shift. Handlers that write must call
 * `findElementPath` when they run, not close over this value.
 */
export const useElementPath = (element: SlateNode): Path | undefined => {
  const editor = useSlateStatic() as unknown as TunaEditor
  return findElementPath(editor, element)
}
